import { useEffect, useRef } from 'react';

/** Селекторы, с которых горизонтальный пан стартовать нельзя */
const BLOCKED_PAN_SELECTOR = [
    '.app-task-card',
    '.column-header',
    'button',
    'a',
    'input',
    'textarea',
    'select',
    'label',
    '.ant-btn',
    '.ant-tag',
    '.ant-badge',
    '.ant-dropdown',
    '.ant-select',
    '.ant-modal',
    '[contenteditable="true"]',
    '[role="button"]',
].join(', ');

/**
 * Горизонтальная прокрутка доски зажатой ЛКМ по пустому месту
 * (не по карточкам, заголовкам колонок и интерактивным элементам).
 */
export function useBoardPanScroll<T extends HTMLElement>() {
    const scrollRef = useRef<T | null>(null);

    useEffect(() => {
        const root = scrollRef.current;
        if (!root) {
            return;
        }

        let active = false;
        let startX = 0;
        let startScrollLeft = 0;

        const isEmptySpace = (target: EventTarget | null) => {
            if (!(target instanceof Element)) {
                return false;
            }
            if (!root.contains(target)) {
                return false;
            }
            return !target.closest(BLOCKED_PAN_SELECTOR);
        };

        const onPointerDown = (event: PointerEvent) => {
            if (event.button !== 0) {
                return;
            }
            if (!isEmptySpace(event.target)) {
                return;
            }
            // Не начинаем пан, если уже тянут карточку/колонку
            if (
                document.querySelector('[data-rbd-dragging]')
                || document.querySelector('[data-rfd-dragging-id]')
            ) {
                return;
            }

            active = true;
            startX = event.clientX;
            startScrollLeft = root.scrollLeft;
            root.classList.add('board-scroll--panning');
            root.setPointerCapture?.(event.pointerId);
            event.preventDefault();
        };

        const onPointerMove = (event: PointerEvent) => {
            if (!active) {
                return;
            }
            const dx = event.clientX - startX;
            root.scrollLeft = startScrollLeft - dx;
        };

        const endPan = (event: PointerEvent) => {
            if (!active) {
                return;
            }
            active = false;
            root.classList.remove('board-scroll--panning');
            try {
                root.releasePointerCapture?.(event.pointerId);
            } catch {
                // capture мог уже сняться
            }
        };

        root.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endPan);
        window.addEventListener('pointercancel', endPan);

        return () => {
            root.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', endPan);
            window.removeEventListener('pointercancel', endPan);
            root.classList.remove('board-scroll--panning');
        };
    }, []);

    return scrollRef;
}

export default useBoardPanScroll;
